/* eslint-disable @next/next/no-img-element */
import useCommon from '@utils/hooks/common';
import {Col, Form, Row, Spin, Statistic, notification, Skeleton as AntSkeleton} from 'antd';
import {Skeleton} from 'antd-mobile';
import CustomInput from '@components/CustomInput';
import {useEffect, useState} from 'react';
import useRequest from '@utils/hooks/useRequestCustom';
import authApi from '@utils/api/auth.api';
import {CopyOutlined, LoadingOutlined} from '@ant-design/icons';
import CustomSelect from '@components/CustomSelect';
import GradientButton from '@components/GradientButton';
import {QRCode} from 'react-qrcode-logo';
import {useWriteContract, useAccount, useSwitchChain} from 'wagmi';
import {mainnet, bsc} from 'viem/chains';
import styles from './styles';
import config from '@configs/WalletConnectConfig';
import {env} from 'next-runtime-env';
import {waitForTransactionReceipt, getBalance} from '@wagmi/core';
import BigNumber from 'bignumber.js';
import WalletConnectConfig from '@configs/WalletConnectConfig';
import {parseUnits} from 'viem';
import ERC20_ABI from '@configs/abi';
import getTokenSymbol from '../getTokenSymbol';
import {useRouter} from 'next/router';
import {isTelegramMiniApp} from '@components/layout';
import {copyTextToClipboard} from '@utils/index';
import DepositMethodSwitch from '../DepositMethodSwitch';

//* if amount is --, improper contractAddress may be a reason

interface Network {
  network: string;
  token: string;
  fee: string;
  decimalUnit: number;
  contractAddress: `0x${string}`;
  networkTokenId: any;
}

interface BalanceInfo {
  decimals: number;
  formatted: string;
  symbol: string;
  value: bigint;
}

interface Balances {
  [key: string]: BalanceInfo;
}

const ERC20_ADDRESS = '0xdAC17F958D2ee523a2206206994597C13D831ec7';

const MOCK_NETWORKS = [
  {
    network: 'ERC20',
    token: 'USDT',
    fee: '0',
    decimalUnit: 18,
    contractAddress: '0x55d398326f99059ff775485246999027b3197955' as `0x${string}`,
    networkTokenId: 'USDT_BSC',
  },
  {
    network: 'ERC20',
    token: 'USDT',
    fee: '0',
    decimalUnit: 6,
    contractAddress: '0xdAC17F958D2ee523a2206206994597C13D831ec7' as `0x${string}`,
    networkTokenId: 'USDT_ETH',
  },
  {
    network: 'TRC20',
    token: 'USDT',
    fee: '0',
    decimalUnit: 6,
    contractAddress: 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t' as any,
    networkTokenId: 'USDT_TRX',
  },
];

const TOKEN_TO_CHAIN_ID = {
  USDT_BSC: 56, // Binance Smart Chain
  USDT_ETH: 1, // ETH
  // add other mappings as needed
};

export const DEFAULT_NETWORK_TOKEN_ID = 'USDT_ETH';
const TON_DEFAULT_TOKEN = 'USDT_TON';

const FORM_INITIAL_VALUES = {blockchain: 'usdt'};

const WalletConnectContent = () => {
  const {classes, t, user, system} = useCommon({
    withTranslation: ['deposit_withdraw'],
    withStyles: styles,
    observers: ['user', 'system'],
  });

  const router = useRouter();
  const isDebug = router && router.query.isDebug === 'true';

  const [form] = Form.useForm();

  const [networks, setNetworks] = useState<Network[]>([]);
  const [selectedToken, setSelectedToken] = useState<
    'USDT_ETH' | 'USDT_BSC' | 'USDT_TRX' | 'USDT_TON'
  >('USDT_ETH');
  const [balances, setBalances] = useState<Balances>({});
  const [isMakingTransaction, setIsMakingTransaction] = useState<boolean>(false);
  const [isLoadingBalance, setIsLoadingBalance] = useState<boolean>(false);
  const [regType, setRegType] = useState<'WALLET' | 'METAMASK' | 'EMAIL'>(null);
  const [clickCount, setClickCount] = useState(0);
  const [usdtTokenBalance, setUsdtTokenBalance] = useState<number | null>(null);
  const [isManualDeposit, setIsManualDeposit] = useState<boolean>(false);

  const {
    data,
    loading: loadingDepositAddress,
    runAsync: getDepositAddress,
  } = useRequest(authApi.depositAddress, {
    onSuccess: result => {
      form.setFieldValue('depositAddress', result?.data?.walletAddress);
    },
  });

  const {data: depositAddress} = data || {};
  const {memo, walletAddress} = depositAddress || {};

  // TODO @@ can use getTokenData from toncenter to check contract_type
  // * https://toncenter.com/api/v2/#/accounts/detect_address_detectAddress_get

  // TODO @@ to true for dev
  const isTg = isTelegramMiniApp;
  // const isTg = true;

  const {data: depositInfo, loading: loadingDepositInfo} = useRequest(authApi.getDepositInfo, {
    onSuccess: result => {
      // console.log('@@ result?.networks', result?.networks);
      if (isDebug) {
        setNetworks(MOCK_NETWORKS);
      } else {
        setNetworks(result?.networks);
      }
      setRegType(result?.registrationType);
      form.setFieldValue('network', DEFAULT_NETWORK_TOKEN_ID);
    },
  });

  const {switchChainAsync} = useSwitchChain({config: WalletConnectConfig});

  const {address: userAddress, chainId: currentChainId} = useAccount({
    config: WalletConnectConfig,
  });

  const {data: hash, writeContractAsync} = useWriteContract({config: WalletConnectConfig});

  // TODO @@ may need to pass gas price to T+
  // const {data: gasPriceData} = useGasPrice({config: WalletConnectConfig});
  // console.log('@@ gasPriceData:', gasPriceData);

  // const {data: balanceData, isLoading: isLoadingBalance} = useBalance({
  //   address: address as `0x${string}`,
  //   // token: usdtTokenAddress,
  //   // TODO @@ handle isDev later, fix production first
  //   token: isDev ? erc20Address : erc20Address,
  //   chainId: 1, // Ethereum mainnet
  // });

  useEffect(() => {
    const fetchBalances = async () => {
      if (networks.length === 0) return;
      setIsLoadingBalance(true);
      const fetchedBalances = {};

      const balancePromises = networks.map(async network => {
        const {contractAddress, networkTokenId} = network;
        const chainId = TOKEN_TO_CHAIN_ID[networkTokenId];

        if (chainId) {
          try {
            const balance = await getBalance(config, {
              address: userAddress as `0x${string}`,
              token: contractAddress as `0x${string}`,
              chainId,
            });

            // console.log('@@ getBalance - chainId, balance', chainId, balance);
            fetchedBalances[networkTokenId] = balance;
          } catch (error) {
            // TODO telegram - unhide later
            // if (system?.isDebugMode) {
            //   notification.error({
            //     message: 'getBalance - error',
            //     description: JSON.stringify(error),
            //   });
            // }
            // console.log(` !! getBalance - error - ${network.networkTokenId}:`, error);
          }
        } else {
          // console.log(` !! getBalance - chainId not found - ${networkTokenId}`);
        }
      });

      await Promise.all(balancePromises);
      setBalances(fetchedBalances);
      setIsLoadingBalance(false);
    };

    fetchBalances();
  }, [userAddress, networks]);

  // * for testing different method to get wallet balance
  useEffect(() => {
    const getETH = async () => {
      try {
        const ethBalance = await getBalance(config, {
          address: userAddress as `0x${string}`,
          token: ERC20_ADDRESS,
          chainId: 1,
        });
        // TODO @@ telegram - unhide later
        // console.log(' useEffect ~ getETH:', ethBalance);
      } catch (error) {
        // console.log(' getETH ~ error:', error);
      }
    };

    const getBNB = async () => {
      try {
        const bnbBalance = await getBalance(config, {
          address: userAddress as `0x${string}`,
          token: '0x55d398326f99059ff775485246999027b3197955',
          chainId: 56,
        });
        // console.log(' useEffect ~ getBNB:', bnbBalance);
      } catch (error) {
        // console.log(' getBNB ~ error:', error);
      }
    };

    getETH();
    getBNB();
  }, [userAddress]);

  // const isWallet = regType === 'WALLET' || regType === 'METAMASK';
  const isWalletAndNotTRX =
    (regType === 'WALLET' || regType === 'METAMASK') &&
    selectedToken !== 'USDT_TRX' &&
    // TODO
    selectedToken !== 'USDT_TON';

  const getChain = (network: 'USDT_BSC' | 'USDT_ETH' | 'USDT_TRX') => {
    if (network === 'USDT_BSC') {
      return bsc;
    }
    if (network === 'USDT_ETH') {
      return mainnet;
    }
    return mainnet;
  };

  const handleClick = () => {
    setClickCount(prevCount => prevCount + 1);
    if (clickCount === 5) {
      notification.success({
        message: 'debug mode on',
      });
      system?.setIsDebugMode(true);
    }
  };

  // TODO @@ T+ bug
  const onDeposit = async ({
    amount,
    contractAddress,
    decimalUnit,
    networkTokenId,
  }: {
    amount: string;
    contractAddress: `0x${string}`;
    decimalUnit: number;
    networkTokenId: 'USDT_BSC' | 'USDT_ETH' | 'USDT_TRX';
  }) => {
    const ERC20_TOKEN_DECIMALS = 6;

    try {
      setIsMakingTransaction(true);

      if (!userAddress) {
        console.log(' @@ onDeposit - no address ');
        return;
      }

      const chain = getChain(networkTokenId);
      const targetChainId = chain.id;

      // Switch chain if wallet is on different network
      if (currentChainId !== targetChainId) {
        console.log(
          ` @@ onDeposit - switching chain from ${currentChainId} to ${targetChainId}`
        );
        try {
          await switchChainAsync({chainId: targetChainId});
        } catch (switchError: any) {
          console.log(' @@ onDeposit - switchChain error:', switchError);
          if (switchError?.cause?.code === 4001) {
            notification.info({message: t(`cancel_transaction`)});
          } else {
            notification.error({
              message: t('switch_network_failed') || 'Please switch network in your wallet',
            });
          }
          return;
        }
      }

      console.log(
        ' ## networkTokenId, contractAddress, decimalUnit, amount, chain, walletAddress:',
        networkTokenId,
        contractAddress,
        decimalUnit,
        amount,
        chain,
        depositAddress?.walletAddress
      );

      // TODO @@ expected: data, from, gas, gasPrice, to, value
      // TODO @@ received: data, from, to
      const transactionResult = await writeContractAsync({
        chain,
        account: userAddress as `0x${string}`,
        chainId: chain?.id,
        address: contractAddress,
        functionName: 'transfer',
        abi: ERC20_ABI,
        // * 'to' will fail the transaction
        // to: receiverAddress,
        args: [depositAddress?.walletAddress, parseUnits(amount, decimalUnit)],
        // gas: '0x4727e',
        // gasPrice: '0xb2d05e00',
        // value: '0x6a39e43ec8c000',
        // overrides: {
        //   gas: BigInt(21000),
        //   gasPrice: BigInt(50_000_000_000),
        //   value: BigInt(0),
        // },
      });

      console.log('onDeposit -  transactionResult', transactionResult);

      if (transactionResult) {
        notification.info({
          message: t('deposit_pending_title'),
          description: t('deposit_pending_description'),
        });
        system?.dismissModalSheet();

        const receipt = await waitForTransactionReceipt(config, {hash: transactionResult as any});

        console.log(' ** onDeposit - receipt', receipt);

        if (receipt?.status === 'success') {
          user?.refreshPlayerInfo();

          notification.success({
            message: t('deposit_success_title'),
            description: t('deposit_success_description'),
          });
          system?.dismissModalSheet();
        }
      }
    } catch (err) {
      console.log('onDeposit - err', JSON.stringify(err));

      if (err?.cause?.code === 4001) {
        notification.info({message: t(`cancel_transaction`)});
      } else {
        console.log('onDeposit - err', err);
        // notification.error({message: err?.shortMessage});
      }
    } finally {
      setIsMakingTransaction(false);
    }
  };

  const handleCopyClick = async (value: string) => {
    if (!value) return;

    try {
      await copyTextToClipboard(value);
      notification.success({message: t('common:copy_success')});
    } catch (error) {
      console.error('Error copying text:', error);
    }
  };

  const renderQrCodeSection = () => {
    return (
      <section className={classes.qrCodeArea} onClick={handleClick}>
        {selectedToken === 'USDT_TON' && memo && (
          <div style={{marginBottom: 10, color: 'red', textAlign: 'center'}}>
            <div>{t('memo_reminder_1', {memo})}</div>
          </div>
        )}
        {depositAddress?.walletAddress ? (
          <QRCode
            style={{width: 135, height: 135}}
            value={depositAddress?.walletAddress}
            logoImage="/icon/deposit-withdraw/usdt.svg"
            removeQrCodeBehindLogo
            logoPaddingStyle="circle"
            logoPadding={2}
          />
        ) : (
          <div className={classes.spinContainer}>
            <Spin size="large" indicator={<LoadingOutlined style={{color: 'white'}} />} />
          </div>
        )}
        <div className={classes.exchangeRateContainer}>
          <span>{`${t('exchange_rate')} (USDT/USD): `}</span>
          <span className={classes.exchangeRate}>{`${depositInfo?.usdtRate || '--'}`}</span>
        </div>
        {selectedToken === 'USDT_TON' && memo && (
          <CustomInput
            value={memo}
            disabled
            suffix={
              <CopyOutlined onClick={() => handleCopyClick(memo)} className={classes.copyIcon} />
            }
          />
        )}
        <div className={classes.depositDescription}>
          <p className={classes.formItemLabel}>{t('deposit_process_message_1')}</p>
        </div>
      </section>
    );
  };

  const renderDepositButton = () => (
    <div className={classes.buttonArea}>
      <GradientButton
        loading={isMakingTransaction}
        style={{height: 50, margin: 0}}
        htmlType="submit"
        className={classes.depositButton}
      >
        {t('deposit')}
      </GradientButton>
    </div>
  );

  const toggleSwitch = () => {
    setIsManualDeposit(!isManualDeposit);
  };

  if (loadingDepositInfo || !regType) return <AntSkeleton paragraph={{rows: 5}} active />;

  return (
    <section className={classes.centeredContainer}>
      <Form
        className={classes.formContainer}
        layout="vertical"
        initialValues={FORM_INITIAL_VALUES}
        form={form}
        onValuesChange={async (changedValues, values) => {
          // console.log('values', changedValues, values);
          const {blockchain, network} = values;
          if (blockchain && network) {
            // setDepositAddress();
          } else {
            form.setFieldValue('depositAddress', '');
            // setQrValue(null);
          }
        }}
        name="deposit"
        scrollToFirstError
        requiredMark={false}
        onFinish={async values => {
          const depositAmountNumber = Number(values.depositAmount);
          const {depositAddress, network, depositAmount} = values;
          console.log(
            ' onFinish - depositAmountNumber, depositAddress, memo:',
            depositAmountNumber,
            depositAddress,
            memo
          );

          const selectedNetwork = networks?.find(item => item?.networkTokenId === network);
          const {networkTokenId, contractAddress, decimalUnit} = selectedNetwork || {};
          // console.log(
          //   ' !! networkTokenId, contractAddress, decimalUnit:',
          //   networkTokenId,
          //   contractAddress,
          //   decimalUnit
          // );
          // await onDeposit(depositAmount);
          await onDeposit({amount: depositAmount, contractAddress, decimalUnit, networkTokenId});
        }}
      >
        {/* wallet balance  */}
        {isWalletAndNotTRX && (
          <Row className={classes.walletBalanceRow}>
            <Col className={classes.walletAmountCol}>{t('wallet_balance')}</Col>

            <Col>
              {isLoadingBalance ? (
                <Skeleton animated className={classes.skeleton} />
              ) : (
                `${balances[selectedToken]?.formatted || ' -- '} ${
                  balances[selectedToken]?.symbol || ''
                }`
              )}
            </Col>
          </Row>
        )}

        {/* currency type */}
        <Form.Item
          name="blockchain"
          label={<span className={classes.formItemLabel}>{t('currency')}</span>}
          rules={[
            {
              required: true,
              message: t('blockchain_required'),
            },
          ]}
        >
          <CustomSelect
            className={classes.customSelect}
            options={[
              {
                label: (
                  <div className={classes.option}>
                    <div className={classes.coinArea}>
                      <img
                        src="/icon/cryptocurrency/usdt.svg"
                        alt="USD"
                        className={classes.coinIcon}
                      />
                      {t('currency_usdt')}
                    </div>
                    <div className={classes.optionBalance}>
                      <Statistic
                        valueStyle={{
                          color: 'rgba(255, 255, 255, 0.5)',
                          fontSize: 14,
                          fontWeight: 400,
                        }}
                        value={user?.getCoinWalletBalance()}
                      />
                    </div>
                  </div>
                ),
                value: 'usdt',
              },
            ]}
          />
        </Form.Item>

        {/* network */}
        <Form.Item
          name="network"
          label={<span className={classes.formItemLabel}>{t('network')}</span>}
          rules={[
            {
              required: true,
            },
          ]}
        >
          {/* <CustomSelect className={classes.customSelect} options={NETWORK_OPTION} /> */}
          <CustomSelect
            className={classes.customSelect}
            // options={NETWORK_OPTION}
            options={networks?.map(item => ({
              label: <span>{`${item?.network} - ${getTokenSymbol(item?.networkTokenId)}`}</span>,
              value: item?.networkTokenId,
            }))}
            onChange={async value => {
              const selectedNetwork = networks?.find(item => item?.networkTokenId === value);
              setSelectedToken(selectedNetwork?.networkTokenId);

              const result = await getDepositAddress({networkTokenId: value});
              console.log(`@@ getDepositAddress - ${value}`, result?.data);
            }}
          />
        </Form.Item>

        {isWalletAndNotTRX && (
          <div style={{marginBottom: 10, width: '100%'}}>
            <DepositMethodSwitch isManualDeposit={isManualDeposit} toggleSwitch={toggleSwitch} />
          </div>
        )}

        {/* exchange rate section  */}
        {isWalletAndNotTRX && !isManualDeposit && (
          <div className={classes.exchangeRateContainer}>
            <span>{`${t('exchange_rate')} (USDT/USD): `}</span>
            <span className={classes.exchangeRate}>{`${depositInfo?.usdtRate || '--'}`}</span>
          </div>
        )}

        {/* deposit amount / deposit address  */}

        <Spin wrapperClassName={classes.fullWidthSpin} spinning={loadingDepositAddress}>
          {isWalletAndNotTRX && !isManualDeposit ? (
            <Form.Item
              name="depositAmount"
              label={<span className={classes.formItemLabel}>{t('deposit_amount')}</span>}
              rules={[
                {
                  required: true,
                  message: t('amount_required'),
                },
                {
                  validator: (rule, value, callback) => {
                    if (BigNumber(value).isGreaterThan(balances[selectedToken]?.formatted)) {
                      callback(t('amount_exceeds_available'));
                      // TODO @@ for debug, un-hide later
                      // } else if (BigNumber(value).isZero() && !isDebug) {
                      //   callback(t('valid_amount'));
                    } else if (Number.isNaN(Number(value))) {
                      callback(t('invalid_number'));
                    } else {
                      callback();
                    }
                  },
                },
              ]}
            >
              <CustomInput
                placeholder={t('deposit_amount_placeholder')}
                className={classes.depositAmountInput}
                suffix={
                  <GradientButton
                    style={{height: 40, borderRadius: 5, margin: 0}}
                    onClick={() => {
                      // TODO @@
                      form.setFieldsValue({depositAmount: balances[selectedToken]?.formatted});
                    }}
                  >
                    {t('max')}
                  </GradientButton>
                }
              />
            </Form.Item>
          ) : (
            <Form.Item
              name="depositAddress"
              label={<span className={classes.formItemLabel}>{t('deposit_address')}</span>}
            >
              <CustomInput
                style={{width: '100%'}}
                disabled
                suffix={
                  <CopyOutlined
                    onClick={() => handleCopyClick(depositAddress?.walletAddress)}
                    className={classes.copyIcon}
                  />
                }
              />
            </Form.Item>
          )}

          {isWalletAndNotTRX && !isManualDeposit ? renderDepositButton() : renderQrCodeSection()}
        </Spin>
      </Form>
    </section>
  );
};

export default WalletConnectContent;
